import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { PoundSterling } from 'lucide-react';
import { useTerminology } from '@/hooks/useTerminology';
import { InfoTooltip } from '@/components/tours/InfoTooltip';
import { useDrillDown, generateCashPositionDrillDown, generateRunwayDrillDown } from '@/hooks/useDrillDown';
import { ClickableNumber } from '@/components/ui/ClickableNumber';
import { HoverUnderlineSpan } from '@/components/ui/HoverUnderlineSpan';
import { usePermissions } from '@/hooks/usePermissions';
import { DashboardMetrics } from '@/services/metricsService';

interface CurrentCashMiniCardProps {
  metrics: DashboardMetrics;
}

export default function CurrentCashMiniCard({ metrics }: CurrentCashMiniCardProps) {
  const { hasPermission } = usePermissions();
  const { showDrillDown } = useDrillDown();
  const { getCashDurationTerm } = useTerminology();

  if (!hasPermission('view_current_cash_mini_card')) {
    return null;
  }

  // Traffic light: based on runway (months of cash remaining)
  // 🟢 Green  = ≥ 3 months (healthy)
  // 🟡 Amber  = 1–3 months (watch)
  // 🔴 Red    = < 1 month  (critical)
  const runway = metrics.runway;
  const badgeBg =
    runway >= 3
      ? 'var(--mc-success-bg)'
      : runway >= 1
      ? 'var(--mc-warning-bg)'
      : 'var(--mc-error-bg)';
  const iconColor =
    runway >= 3
      ? 'var(--mc-success-icon)'
      : runway >= 1
      ? 'var(--mc-warning-icon)'
      : 'var(--mc-error-icon)';

  const borderColor =
    runway >= 3
      ? 'var(--mc-success-border)'
      : runway >= 1
      ? 'var(--mc-warning-border)'
      : 'var(--mc-error-border)';

  return (
    <Card data-tour="current-cash" className="h-full" style={{ borderLeft: `3px solid ${borderColor}` }}>
      <CardContent className="p-4 flex flex-col justify-between h-full">
        {/* Top row — label + icon badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-base font-medium text-neutral-800 truncate">Current Cash</span>
            <InfoTooltip
              title="Current Cash"
              content="Shows your total cash across all connected bank accounts. Updates in real-time as transactions sync from Access Financials."
            />
          </div>
          <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: badgeBg }}>
            <PoundSterling className="h-4 w-4" style={{ color: iconColor }} />
          </div>
        </div>

        {/* Value + sub-text */}
        <div>
          <div className="text-2xl font-bold text-neutral-900 tracking-tight">
            <ClickableNumber
              value={metrics.currentCash}
              onClick={() => showDrillDown('cash-position', generateCashPositionDrillDown(metrics.currentCash))}
            />
          </div>
          <div className="flex items-center gap-1 mt-1">
            <HoverUnderlineSpan
              className="text-xs font-semibold text-neutral-800 cursor-pointer"
              onClick={() => showDrillDown('runway', generateRunwayDrillDown(metrics.currentCash, metrics.dailyBurn, metrics.runway))}
            >
              {metrics.runway.toFixed(1)} months
            </HoverUnderlineSpan>
            <span className="text-xs font-normal text-neutral-800">
              {getCashDurationTerm().split(' ').slice(2).join(' ') || getCashDurationTerm()}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
